import { FunctionComponent } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles } from '../../../../common/hooks/useStyles';
import { getFullScreenButtonStyleObject } from './fullScreenButton.style';
import { Icon } from '../../../../common/components/icon/icon';
import { setFullscreen, setUserInteraction, setShowControls } from '../../store/controlsPlugin.actions';
import { playerSettingsSelector } from '../../../../common/store/selectors/playerSettings.selector';
import { CONTROLS_ICON_TYPES } from '../../icon/iconMapper';
import { TOOLTIP_POSITIONS } from '../../../../common/components/toolTip/toolTip.contants';
import { ToolTip } from '../../../../common/components/toolTip/toolTip';
import { TOOLTIP_TITLES } from '../../controlsPlugin.constants';
import { playerDataSelector } from '../../../../common/store/selectors/playerData.selector';
import { useHoverRef } from '../../../../common/hooks/useHoverRef';
import { adsDataSelector } from '../../../../common/store/selectors/adsData.selector';
import { getFullscreenId, getFullscreenTooltipId } from '../../htmlElements/htmlElementsIdGetter';
import { isDesktopMode } from '../../../../common/utils/utils';
import { css } from '../../../../common/styles/styles.utils';
import { UserInteractionTypes } from '../../../../common/store/store.types';

const ARIA_LABEL = 'Full screen';

interface FullscreenButtonComponentVariablesProps {
  outsideControlsBar?: boolean;
}

export const FullscreenButton: FunctionComponent<FullscreenButtonComponentVariablesProps> = props => {
  const { outsideControlsBar } = props;

  const dispatch = useDispatch();
  const isFullscreenOn = useSelector(playerSettingsSelector.isFullscreenOn);
  const playerMode = useSelector(playerDataSelector.playerMode);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const isVastAd = useSelector(adsDataSelector.isVastAd);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqId);
  const showControls = useSelector(playerDataSelector.showControls);

  const [fullscreenRef, fullscreenHovered] = useHoverRef();

  const desktopMode = isDesktopMode(playerMode);
  const showAdControls = isVastAd && adBreak;
  const mediumOrSmallSize = playerSize === 'small' || playerSize === 'medium';
  const showFullscreenButton = !outsideControlsBar || showControls || (showAdControls && !desktopMode);

  const styles = useStyles(getFullScreenButtonStyleObject, [playerSize, fullscreenHovered, showAdControls, desktopMode, outsideControlsBar]);
  const { fullScreenIconStyle, fullscreenIconWrapperStyle, iconAndToolTipContainerStyle } = styles;

  const onClickIcon = (e: MouseEvent): void => {
    e.stopPropagation();
    const fullscreenRequestType = !isFullscreenOn ? 'enter' : 'exit';
    dispatch(setFullscreen(fullscreenRequestType));
    dispatch(setUserInteraction(`${fullscreenRequestType}Fullscreen` as UserInteractionTypes));
  };

  const onFullscreenFocus = (): void => {
    dispatch(setShowControls(true));
  };

  const iconProps = {
    id: getFullscreenId(playerUniqueId),
    icon: isFullscreenOn ? CONTROLS_ICON_TYPES.FULLSCREEN_EXIT : CONTROLS_ICON_TYPES.FULLSCREEN_ENTER,
    style: fullScreenIconStyle,
    ariaLabel: ARIA_LABEL,
  };

  const FullScreenIconClickableWrapper: FunctionComponent = () => (
    <div
      ref={fullscreenRef}
      style={css(fullscreenIconWrapperStyle)}
    >
      <Icon
        {...iconProps}
        onClick={onClickIcon}
      />
    </div>
  );

  const FullScreenIcon: FunctionComponent = () => (
    (!desktopMode && mediumOrSmallSize
      ? <FullScreenIconClickableWrapper />
      : (
        <div ref={fullscreenRef}>
          <Icon
            {...iconProps}
            onClick={onClickIcon}
            onControlFocus={onFullscreenFocus}
          />
        </div>
      )
    )
  );

  return (
    showFullscreenButton && (
      desktopMode
        ? (
          <div style={iconAndToolTipContainerStyle}>
            <ToolTip
              id={getFullscreenTooltipId(playerUniqueId)}
              content={isFullscreenOn ? TOOLTIP_TITLES.FULLSCREEN_EXIT : TOOLTIP_TITLES.FULLSCREEN}
              position={outsideControlsBar ? TOOLTIP_POSITIONS.BOTTOM_RIGHT : TOOLTIP_POSITIONS.TOP_LEFT}
              variables={outsideControlsBar && { marginBottom: '0px' }}
            >
              <FullScreenIcon />
            </ToolTip>
          </div>
        )
        : (
          <FullScreenIcon />
        )
    )
  );
};
